import React, {
  ComponentType,
  createContext,
  FC,
  forwardRef,
  PropsWithChildren,
  useContext,
} from "react";
import { developmentConfig } from "./config/developmentConfig";
import { LegolandConfig } from "./config/LegolandConfig";
import { productionConfig } from "./config/productionConfig";
import { testConfig } from "./config/testConfig";

export type LegolandLink = keyof HTMLElementTagNameMap | ComponentType<any>;

export interface LegolandContextValue extends LegolandConfig {
  ExternalLink: LegolandLink;
  InternalLink: LegolandLink;
  Link: LegolandLink;
}

export interface LegolandProviderProps extends Partial<LegolandConfig> {
  /* Environment at which the app is running (default = 'auto'). 'auto' tries to determine it based on the NODE_ENV environment variable. */
  environment?: "auto" | "production" | "development" | "test";
  /* Component used to render links to external URLs (outside of the app) (default = 'a'). */
  externalLinkComponent?: LegolandLink;
  /* Component used to render links to the app's URLs (default = 'a'). */
  internalLinkComponent?: LegolandLink;
}

const uninitializedContextValue = new Proxy(
  {},
  {
    get() {
      throw new Error(
        "Legoland context is not available. Please use LegolandProvider or LegolandApp component."
      );
    },
  }
) as LegolandContextValue;

const LegolandContext = createContext<LegolandContextValue>(
  uninitializedContextValue
);

export const LegolandConsumer = LegolandContext.Consumer;

export function useLegoland() {
  return useContext(LegolandContext);
}

export const LegolandProvider: FC<PropsWithChildren<LegolandProviderProps>> = ({
  children,
  environment = "auto",
  externalLinkComponent: ExternalLink = "a",
  internalLinkComponent: InternalLink = "a",
  ...configFromProps
}) => {
  let config: Readonly<LegolandConfig>;

  switch (environment) {
    case "auto":
      config =
        process.env.NODE_ENV === "production"
          ? productionConfig
          : developmentConfig;
      break;
    case "development":
      config = developmentConfig;
      break;

    case "test":
      config = testConfig;
      break;
    case "production":
      config = productionConfig;
      break;
  }

  const Link: LegolandLink = forwardRef((props, ref) => {
    const { href } = props;
    const Link =
      !href?.startsWith("http") || href?.startsWith(window.location.origin)
        ? InternalLink
        : ExternalLink;
    return <Link {...props} ref={ref} />;
  });

  const value: Readonly<LegolandContextValue> = {
    ...config,
    ...configFromProps,
    ExternalLink,
    InternalLink,
    Link,
  };

  return (
    <LegolandContext.Provider value={value}>
      {children}
    </LegolandContext.Provider>
  );
};
